import React, { useState, useMemo, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form as FormikForm, useField, useFormikContext } from "formik";
import { Card, Form, Button, Row, Col, Spinner } from "react-bootstrap";
import {
  ImagePlus,
  X,
  ArrowLeft,
  UploadCloud,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { uploadImage, createPartner, updatePartner, getPartnerDetail, getPackage, assignPartnerPlan, updatePartnerDetail } from "../../services/UserServices/UserService";
import { getImageUrl, extractImageUrl } from "../../utils/imageUtils";
import Select from "react-select";
import citiesData from "../../utils/cities.json";
import PageTitle from "../layouts/PageTitle";

// Validation Regexes
const GST_REGEX = /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}Z[A-Z\d]{1}$/;
const INSTAGRAM_REGEX = /^(https?:\/\/)?(www\.)?instagram\.com\/[a-zA-Z0-9._%#?&=/\-]+$/i;
const URL_REGEX = /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/;

const BUSINESS_TYPE_OPTIONS = [
  { label: "Wedding Planner", value: "wedding_planner" },
  { label: "Event Management Company", value: "event_management" },
  { label: "Venue / Banquet / Farmhouse", value: "venue" },
  { label: "Decorator / Production Partner", value: "decorator" },
  { label: "Invitation / Creative Agency", value: "invitation" },
  { label: "Hospitality / Travel Partner", value: "hospitality" },
  { label: "Other Wedding Service Provide", value: "other" },
];

const EMOJI_REGEX = /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F780}-\u{1F7FF}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{2300}-\u{23FF}]/u;
const INVALID_SPECIAL_CHARS_REGEX = /[@#$%^&*()!+={\}\[\]|\\:;"<>?/~_]/;

const validateNameCustom = (val, createError) => {
  if (!val) return true;
  const trimmed = val.trim();
  if (/^['’`]/.test(trimmed)) {
    return createError({ message: "Name cannot start with an apostrophe" });
  }
  if (/['’`]$/.test(trimmed)) {
    return createError({ message: "Name cannot end with an apostrophe" });
  }
  if (/['’`]{2,}/.test(trimmed)) {
    return createError({ message: "Name cannot contain consecutive apostrophes" });
  }
  if (/^\d+$/.test(trimmed)) {
    return createError({ message: "Name cannot be numeric-only" });
  }
  if (EMOJI_REGEX.test(trimmed)) {
    return createError({ message: "Name cannot contain emojis" });
  }
  if (INVALID_SPECIAL_CHARS_REGEX.test(trimmed)) {
    return createError({ message: "Name contains invalid special characters (e.g. @, #, $, %)" });
  }
  return true;
};

const getPartnerSchema = () =>
  Yup.object().shape({
    name: Yup.string()
      .trim()
      .min(2, "Partner name must be at least 2 characters")
      .max(50, "Partner name cannot exceed 50 characters")
      .required("Partner name is required")
      .test("custom-name-validation", function (val) {
        return validateNameCustom(val, this.createError.bind(this));
      }),

    companyName: Yup.string()
      .trim()
      .min(2, "Company name must be at least 2 characters")
      .max(100, "Company name cannot exceed 100 characters")
      .required("Company name is required")
      .test("custom-company-name-validation", function (val) {
        return validateNameCustom(val, this.createError.bind(this));
      }),
   countryCode: Yup.string().trim().optional(),
   phoneNumber: Yup.string()
      .trim()
      .required("Phone number is required")
      .matches(/^\d{10}$/, "Enter a valid 10-digit phone number"),

    alternatePhoneNumber: Yup.string()
      .trim()
      .optional()
      .test("valid-alt-phone", "Enter a valid 10-digit phone number", (val) => {
        if (!val) return true;
        return /^\d{10}$/.test(val);
      }),

    businessType: Yup.string()
      .trim()
      .required("Business type is required"),
    customBusinessType: Yup.string().trim().optional(),

    city: Yup.string()
      .trim()
      .min(2, "City must be at least 2 characters")
      .max(50, "City name cannot exceed 50 characters")
      .required("City is required"),



    gstNumber: Yup.string()
      .trim()
      .optional()
      .matches(GST_REGEX, {
        message: "Enter a valid 15-character GST number (e.g. 22AAAAA0000A1Z5)",
        excludeEmptyString: true,
      }),

    website: Yup.string()
      .trim()
      .optional()
      .matches(URL_REGEX, {
        message: "Enter a valid URL (e.g. https://example.com)",
        excludeEmptyString: true,
      }),

    instagram: Yup.string()
      .trim()
      .optional()
      .matches(INSTAGRAM_REGEX, {
        message: "Enter a valid Instagram URL (e.g. https://instagram.com/yourpage)",
        excludeEmptyString: true,
      }),

    aquisitionSource: Yup.string().trim().optional(),
    customAquisitionSource: Yup.string().trim().optional(),
    portfolioImages: Yup.mixed()
      .optional()
      .test(
        "max-portfolio-images",
        "You cannot add or edit more than 10 portfolio images",
        (val) => !val || (Array.isArray(val) && val.length <= 10)
      ),
  });

const FormField = ({ name, label, placeholder, required = false, type = "text", className = "", disabled = false, style: customStyle = {}, ...rest }) => {
  const [field, meta] = useField(name);
  const { submitCount } = useFormikContext();
  const isInvalid = (meta.touched || submitCount > 0) && Boolean(meta.error);
  const isDisabled = disabled || rest.disabled;

  return (
    <Form.Group className="mb-0" controlId={name}>
      <style>{`
        .custom-placeholder-light {
          color: #0f172a !important;
          font-weight: 500 !important;
        }
        .custom-placeholder-light::placeholder,
        .custom-placeholder-light::-webkit-input-placeholder,
        .custom-placeholder-light::-moz-placeholder,
        .custom-placeholder-light:-ms-input-placeholder {
          color: #a0aec0 !important;
          opacity: 0.85 !important;
          font-weight: 400 !important;
        }
        .form-control:disabled,
        .form-control[disabled],
        .custom-placeholder-light:disabled {
          background-color: #f1f5f9 !important;
          color: #64748b !important;
          cursor: not-allowed !important;
          opacity: 1 !important;
        }
        .form-control.is-invalid,
        .form-select.is-invalid,
        .custom-placeholder-light.is-invalid {
          border: 1px solid #dc3545 !important;
          border-radius: 0.5rem !important;
          background-clip: padding-box !important;
        }
        .input-group > .form-control.is-invalid {
          border-top-left-radius: 0 !important;
          border-bottom-left-radius: 0 !important;
          border-top-right-radius: 0.5rem !important;
          border-bottom-right-radius: 0.5rem !important;
        }
      `}</style>
      <Form.Label className="qx-form-label">
        {label} {required && <span className="text-danger">*</span>}
      </Form.Label>
      <div className="position-relative">
        <Form.Control
          {...field}
          type={type}
          placeholder={placeholder}
          isInvalid={isInvalid}
          disabled={isDisabled}
          className={`custom-placeholder-light ${className}`}
          style={{
            paddingLeft: 14,
            borderRadius: "8px",
            height: "44px",
            boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
            borderColor: isInvalid ? "#dc3545" : "#d0d5dd",
            backgroundColor: isDisabled ? "#f9fafb" : "#ffffff",
            color: isDisabled ? "#667085" : "#27272a",
            cursor: isDisabled ? "not-allowed" : "text",
            ...customStyle,
          }}
          {...rest}
        />
        {isInvalid && (
          <Form.Control.Feedback type="invalid" className="d-block mt-1 small">
            {meta.error}
          </Form.Control.Feedback>
        )}
      </div>
    </Form.Group>
  );
};

const PhoneField = ({ disabled = false }) => {
  const [field, meta] = useField("phoneNumber");
  const { submitCount } = useFormikContext();
  const isInvalid = (meta.touched || submitCount > 0) && Boolean(meta.error);

  return (
    <Form.Group className="mb-0" controlId="phoneNumber">
      <Form.Label className="qx-form-label">
        Phone Number {!disabled && <span className="text-danger">*</span>}
      </Form.Label>
      <div className={`qx-phone-input ${isInvalid ? "is-invalid" : ""}`}>
        <span className="qx-phone-prefix">+91</span>
        <Form.Control
          {...field}
          type="text"
          placeholder="eg. 9876543210"
          disabled={disabled}
          maxLength={10}
          className="qx-phone-control qx-keep custom-placeholder-light"
        />
      </div>
      {isInvalid && (
        <div className="invalid-feedback d-block mt-1">{meta.error}</div>
      )}
    </Form.Group>
  );
};

const AlternatePhoneField = ({ disabled = false }) => {
  const [field, meta] = useField("alternatePhoneNumber");
  const { submitCount } = useFormikContext();
  const isInvalid = (meta.touched || submitCount > 0) && Boolean(meta.error);

  return (
    <Form.Group className="mb-0" controlId="alternatePhoneNumber">
      <Form.Label className="qx-form-label">
        Alternate Phone Number <span className="text-muted text-lowercase fw-normal">(optional)</span>
      </Form.Label>
      <div className={`qx-phone-input ${isInvalid ? "is-invalid" : ""}`}>
        <span className="qx-phone-prefix">+91</span>
        <Form.Control
          {...field}
          type="text"
          placeholder="eg. 9876543210"
          disabled={disabled}
          maxLength={10}
          className="qx-phone-control qx-keep custom-placeholder-light"
        />
      </div>
      {isInvalid && (
        <div className="invalid-feedback d-block mt-1">{meta.error}</div>
      )}
    </Form.Group>
  );
};

const BusinessTypeField = () => {
  const [field, meta] = useField("businessType");
  const [customField] = useField("customBusinessType");
  const { values, submitCount } = useFormikContext();
  const isInvalid = (meta.touched || submitCount > 0) && Boolean(meta.error);
  const showOtherInput = values.businessType === "other";

  return (
    <div className="mb-0">
      <Form.Group controlId="businessType" className="mb-0">
        <Form.Label className="qx-form-label">
          Business Type <span className="text-danger">*</span>
        </Form.Label>
        <Form.Select
          {...field}
          isInvalid={isInvalid}
          className="custom-placeholder-light"
          style={{
            borderRadius: "8px",
            height: "44px",
            borderColor: isInvalid ? "#dc3545" : "#d0d5dd",
            boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
          }}
        >
          <option value="">Select Business Type</option>
          {BUSINESS_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Form.Select>
        {isInvalid && (
          <div className="invalid-feedback d-block mt-1">{meta.error}</div>
        )}
      </Form.Group>

      {showOtherInput && (
        <Form.Group controlId="customBusinessType" className="mt-3 mb-0">
          <Form.Label className="qx-form-label">
            Mention Other <span className="text-muted text-lowercase fw-normal">(optional)</span>
          </Form.Label>
          <Form.Control
            {...customField}
            type="text"
            placeholder="Mention other business type..."
            className="custom-placeholder-light"
            style={{
              paddingLeft: 14,
              borderRadius: "8px",
              height: "44px",
              boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
              borderColor: "#d0d5dd",
            }}
          />
        </Form.Group>
      )}
    </div>
  );
};



const CitySelectField = () => {
  const [field, meta] = useField("city");
  const { setFieldValue, submitCount } = useFormikContext();
  const isInvalid = (meta.touched || submitCount > 0) && Boolean(meta.error);

  const cityOptions = useMemo(() => {
    return (citiesData || []).map((item) => ({
      value: item.name,
      label: `${item.name} (${item.state})`,
    }));
  }, []);

  const selectedOption = useMemo(() => {
    if (!field.value) return null;
    const found = cityOptions.find(
      (opt) => opt.value.toLowerCase() === String(field.value).toLowerCase()
    );
    return found || { value: field.value, label: field.value };
  }, [field.value, cityOptions]);

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "44px",
      borderRadius: "8px",
      borderColor: isInvalid
        ? "#dc3545"
        : state.isFocused
          ? "#A60000"
          : "#d0d5dd",
      boxShadow: state.isFocused
        ? "0 0 0 3px rgba(166, 0, 0, 0.15)"
        : "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
      "&:hover": {
        borderColor: isInvalid ? "#dc3545" : "#A60000",
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "2px 12px",
    }),
    input: (provided) => ({
      ...provided,
      margin: 0,
      padding: 0,
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 30,
    }),
  };

  return (
    <Form.Group className="mb-0" controlId="city">
      <Form.Label className="qx-form-label">
        City <span className="text-danger">*</span>
      </Form.Label>
      <Select
        className="qx-city-select"
        classNamePrefix="qx-select"
        options={cityOptions}
        value={selectedOption}
        onChange={(option) => {
          setFieldValue("city", option ? option.value : "");
        }}
        onBlur={() => field.onBlur({ target: { name: "city" } })}
        placeholder="Select or search city..."
        isClearable
        isSearchable
        styles={customSelectStyles}
      />
      {isInvalid && (
        <div className="invalid-feedback d-block mt-1">{meta.error}</div>
      )}
    </Form.Group>
  );
};

const AcquisitionSourceField = () => {
  const [field, meta] = useField("aquisitionSource");
  const [customField] = useField("customAquisitionSource");
  const { values, submitCount } = useFormikContext();
  const isInvalid = (meta.touched || submitCount > 0) && Boolean(meta.error);
  const showOtherInput = values.aquisitionSource === "others" || values.aquisitionSource === "other";

  return (
    <div className="mb-0">
      <Form.Group controlId="aquisitionSource" className="mb-0">
        <Form.Label className="qx-form-label">
          Acquisition Source <span className="text-muted text-lowercase fw-normal">(optional)</span>
        </Form.Label>
        <Form.Select
          {...field}
          isInvalid={isInvalid}
          className="custom-placeholder-light"
          style={{
            borderRadius: "8px",
            height: "44px",
            borderColor: isInvalid ? "#dc3545" : "#d0d5dd",
            boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
          }}
        >
          <option value="">Select Acquisition Source</option>
          <option value="app">App</option>
          <option value="website">Website</option>
          <option value="others">Others</option>
        </Form.Select>
        {isInvalid && (
          <div className="invalid-feedback d-block mt-1">{meta.error}</div>
        )}
      </Form.Group>

      {showOtherInput && (
        <Form.Group controlId="customAquisitionSource" className="mt-3 mb-0">
          <Form.Label className="qx-form-label">
            Specify Other Source <span className="text-muted text-lowercase fw-normal">(optional)</span>
          </Form.Label>
          <Form.Control
            {...customField}
            type="text"
            placeholder="Specify acquisition source..."
            className="custom-placeholder-light"
            style={{
              paddingLeft: 14,
              borderRadius: "8px",
              height: "44px",
              boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
              borderColor: "#d0d5dd",
            }}
          />
        </Form.Group>
      )}
    </div>
  );
};

const parsePhone = (phoneStr) => {
  if (!phoneStr) return "";
  const cleaned = phoneStr.trim().replace(/\D/g, "");
  if (cleaned.length === 12 && cleaned.startsWith("91")) {
    return cleaned.slice(2);
  }
  return cleaned.slice(-10);
};

export default function CreatePartner() {
  const navigate = useNavigate();
  const location = useLocation();
  const partner = location.state?.partner;
  const partnerId = partner?._id || partner?.id;

  const [fetchedPartner, setFetchedPartner] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);


  useEffect(() => {
    if (partnerId) {
      setLoadingDetail(true);
      getPartnerDetail(partnerId)
        .then((res) => {
          const detailData = res?.data?.data || res?.data;
          if (detailData) {
            setFetchedPartner(detailData);
          }
        })
        .catch((err) => {
          console.error("Error fetching partner details:", err);
        })
        .finally(() => {
          setLoadingDetail(false);
        });
    }
  }, [partnerId]);

  const activePartner = fetchedPartner || partner;
  const pData = activePartner?.partnerData || {};

  const getInitialPortfolio = (partnerObj) => {
    if (!partnerObj) return [];
    const p = partnerObj.partnerData || partnerObj;
    if (Array.isArray(p.portfolioImages)) return p.portfolioImages;
    if (p.portfolioImages) return [p.portfolioImages];
    if (Array.isArray(p.portfolioImage)) return p.portfolioImage;
    if (p.portfolioImage) return [p.portfolioImage];
    if (Array.isArray(p.images)) return p.images;
    return [];
  };

  const [logoPreview, setLogoPreview] = useState(pData.logo || activePartner?.logo || null);
  const [galleryPreviews, setGalleryPreviews] = useState(getInitialPortfolio(activePartner));
  const [isLogoUploading, setIsLogoUploading] = useState(false);
  const [isGalleryUploading, setIsGalleryUploading] = useState(false);

  const isEdit = location.pathname.includes("edit-partner");
  const [packagesList, setPackagesList] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(false);

  useEffect(() => {
    if (fetchedPartner) {
      const p = fetchedPartner.partnerData || fetchedPartner;
      setLogoPreview(p.logo || fetchedPartner.logo || null);
      setGalleryPreviews(getInitialPortfolio(fetchedPartner));
    }
  }, [fetchedPartner]);

  useEffect(() => {
    if (!isEdit) {
      setLoadingPackages(true);
      getPackage(false)
        .then((res) => {
          const list = res?.data?.data || res?.data || [];
          setPackagesList(Array.isArray(list) ? list : []);
        })
        .catch((err) => console.error("Error fetching packages:", err))
        .finally(() => setLoadingPackages(false));
    }
  }, [isEdit]);

  const initialValues = useMemo(() => {
    const p = activePartner?.partnerData || {};
    const parsedPhone = parsePhone(activePartner?.phoneNumber || activePartner?.phone || p.phoneNumber || p.phone);
    const parsedAltPhone = parsePhone(activePartner?.alternatePhoneNumber || activePartner?.alternatePhone || p.alternatePhoneNumber || p.alternatePhone);

    const rawBusinessType = p.businessType || activePartner?.businessType || "";
    const KNOWN_BUSINESS_TYPES = [
      "wedding_planner",
      "event_management",
      "venue",
      "decorator",
      "invitation",
      "hospitality",
    ];
    const isKnownBusinessType = KNOWN_BUSINESS_TYPES.includes(String(rawBusinessType).toLowerCase());
    const selectBusinessType = isKnownBusinessType
      ? String(rawBusinessType).toLowerCase()
      : (rawBusinessType ? "other" : "");
    const customBusinessType = isKnownBusinessType ? "" : rawBusinessType;

    const rawSource =
      p.aquisitionSource ||
      p.acquisitionSource ||
      p.vendorAcquisitionSource ||
      activePartner?.aquisitionSource ||
      activePartner?.acquisitionSource ||
      activePartner?.vendorAcquisitionSource ||
      "";

    const isKnownOption = ["app", "website"].includes(String(rawSource).toLowerCase());
    const selectSource = isKnownOption ? String(rawSource).toLowerCase() : (rawSource ? "others" : "");
    const customSource = isKnownOption ? "" : rawSource;

    return {
      countryCode: "+91",
      phoneNumber: parsedPhone,
      alternatePhoneNumber: parsedAltPhone,
      businessType: selectBusinessType,
      customBusinessType: customBusinessType,
      name: activePartner?.hostName || activePartner?.name || activePartner?.ownerName || "",
      companyName: p.companyName || activePartner?.companyName || "",
      email: p.email || activePartner?.email || "",
      city: p.city || activePartner?.city || "",
      gstNumber: p.gstNumber || activePartner?.gstNumber || "",
      aquisitionSource: selectSource,
      customAquisitionSource: customSource,
      website: p.website || activePartner?.website || "",
      instagram: p.instagram || activePartner?.instagram || "",
      portfolioImages: getInitialPortfolio(activePartner),
      logo: p.logo || activePartner?.logo || "",
      subscriptionId: "",
      actualAmount: "",
      receivedMoney: "",
    };
  }, [activePartner]);

  const handleLogoChange = async (e, setFieldValue) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsLogoUploading(true);

    try {
      const formData = new FormData();
      formData.append("image", file);

      const res = await uploadImage(formData);
      const url = extractImageUrl(res);

      if (url) {
        setFieldValue("logo", url);
        setLogoPreview(url);
        toast.success("Logo uploaded successfully!");
      } else {
        toast.error("Failed to extract uploaded logo URL");
      }
    } catch (error) {
      console.error("Error uploading logo:", error);
      toast.error("Failed to upload logo image");
    } finally {
      setIsLogoUploading(false);
    }
  };

  const handleGalleryChange = async (e, values, setFieldValue) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const currentList = Array.isArray(values.portfolioImages) ? values.portfolioImages : galleryPreviews;
    const currentCount = currentList.length;

    if (currentCount >= 10) {
      toast.error("Maximum 10 portfolio images allowed. You cannot add or edit more than 10 images.");
      e.target.value = "";
      return;
    }

    const availableSlots = 10 - currentCount;
    if (files.length > availableSlots) {
      toast(`Maximum limit is 10 portfolio images. Only ${availableSlots} image(s) can be added.`, { icon: "⚠️" });
    }

    const filesToUpload = files.slice(0, availableSlots);

    setIsGalleryUploading(true);
    let updatedList = [...currentList];
    for (const file of filesToUpload) {
      try {
        const formData = new FormData();
        formData.append("image", file);
        formData.append("file", file);
        const res = await uploadImage(formData);
        const url = extractImageUrl(res);

        if (url) {
          updatedList = [...updatedList, url];
          setFieldValue("portfolioImages", updatedList);
          setGalleryPreviews(updatedList);
          toast.success("Portfolio image uploaded successfully!");
        } else {
          toast.error("Failed to extract portfolio image URL");
        }
      } catch (error) {
        console.error("Error uploading gallery image:", error);
        toast.error("Failed to upload portfolio image");
      }
    }
    setIsGalleryUploading(false);
    e.target.value = "";
  };

  const removeGalleryImage = (index, values, setFieldValue) => {
    const currentList = Array.isArray(values.portfolioImages) ? values.portfolioImages : galleryPreviews;
    const updatedList = currentList.filter((_, i) => i !== index);
    setFieldValue("portfolioImages", updatedList);
    setGalleryPreviews(updatedList);
  };

  const handleSubmit = async (values) => {
    const finalAquisitionSource =
      (values.aquisitionSource === "others" || values.aquisitionSource === "other")
        ? (values.customAquisitionSource ? values.customAquisitionSource.trim() : "others")
        : (values.aquisitionSource ? values.aquisitionSource.trim() : "");

    const finalBusinessType =
      values.businessType === "other"
        ? (values.customBusinessType ? values.customBusinessType.trim() : "")
        : (values.businessType ? values.businessType.trim() : "");

    const responsePayload = {
      name: values.name ? values.name.trim() : "",
      companyName: values.companyName ? values.companyName.trim() : "",
      email: values.email ? values.email.trim() : "",
      countryCode: "+91",
      phoneNumber: values.phoneNumber ? values.phoneNumber.trim() : "",
      alternatePhoneNumber: values.alternatePhoneNumber ? values.alternatePhoneNumber.trim() : "",
      businessType: finalBusinessType,
      city: values.city ? values.city.trim() : "",
      gstNumber: values.gstNumber ? values.gstNumber.trim() : "",
      aquisitionSource: finalAquisitionSource,
      website: values.website ? values.website.trim() : "",
      instagram: values.instagram ? values.instagram.trim() : "",
      logo: values.logo || "",
      portfolioImages: Array.isArray(values.portfolioImages)
        ? values.portfolioImages
        : values.portfolioImages
          ? [values.portfolioImages]
          : [],
    };

    const targetPartnerId = activePartner?._id || activePartner?.id || partnerId;

    if (!isEdit && values.subscriptionId) {
      responsePayload.subscriptionId = values.subscriptionId;
      responsePayload.receiverAmount = values.receivedMoney || "";
    }

    if (isEdit) {
      responsePayload.partnerId = targetPartnerId;
    } else if (targetPartnerId) {
      responsePayload.partnerId = targetPartnerId;
    }

    console.log("Create/Update Partner Payload:", responsePayload);

    try {
      if (isEdit) {
        // Edit Partner from Partner Details (/edit-partner)
        await updatePartnerDetail(responsePayload);
        toast.success("Partner updated successfully!");
      } else if (targetPartnerId) {
        // Individual row Create Partner from Interested Partners table
        await updatePartner(responsePayload);

        toast.success("Partner created successfully!");
      } else {
        // Global "+ Create Partner" button
        const createRes = await createPartner(responsePayload);
        const newPartnerId = createRes?.data?.data?._id || createRes?.data?._id || createRes?.data?.id;

        toast.success("Partner created successfully!");
      }
      navigate(-1);
    } catch (error) {
      console.error("Error saving partner:", error);
      toast.error(error?.response?.data?.message || "Failed to save partner");
    }
  };

  return (
    <div className="qx-page qx-partner-form-page">
      <PageTitle
        pageTitle={isEdit ? "Edit Partner Details" : "Create New Partner"}
        subtitle={
          isEdit
            ? "Update the details below for this business partner"
            : "Fill in the details below to add a new business partner"
        }
        breadcrumbs={[
          { label: "Partners", path: "/partners" },
          { label: isEdit ? "Edit Partner" : "Create Partner" },
        ]}
        actions={
          <Button
            variant="outline-secondary"
            className="d-inline-flex align-items-center gap-2"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={16} />
            Back
          </Button>
        }
      />
      {loadingDetail && (
        <div className="d-flex align-items-center gap-2 mb-3 text-muted small">
          <Spinner animation="border" size="sm" style={{ color: "#A60000" }} />
          Loading partner details…
        </div>
      )}

      <Card className="premium-card qx-section-card qx-partner-form-card">
        <Card.Body className="qx-partner-form-card">
          <Formik
            initialValues={initialValues}
            enableReinitialize
            validationSchema={getPartnerSchema()}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                await handleSubmit(values);
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ values, setFieldValue, isSubmitting, errors }) => (
              <FormikForm noValidate>

                {/* SECTION 1: Company Logo & Branding */}
                <div className="qx-form-section">
                  <div className="qx-form-section-head">
                    <span className="qx-form-section-num">1</span>
                    <div className="qx-form-section-head-text">
                      <h6 className="qx-form-section-title">Company Logo & Branding</h6>
                      <p className="qx-form-section-desc">Upload your partner company logo</p>
                    </div>
                  </div>
                  <div className="qx-logo-upload">
                    <label htmlFor="logo-upload" className="qx-logo-preview mb-0">
                      {logoPreview || values.logo ? (
                        <img
                          src={getImageUrl(logoPreview || values.logo)}
                          alt="Company logo preview"
                        />
                      ) : (
                        <div className="qx-logo-placeholder">
                          <ImagePlus size={24} />
                          <span>Upload</span>
                        </div>
                      )}
                    </label>
                    <div className="qx-logo-upload-meta">
                      <p className="qx-logo-upload-meta__title">Company Logo</p>
                      <p className="qx-logo-upload-meta__hint">
                        {isLogoUploading ? "Uploading..." : "PNG or JPG, max 5MB"}
                      </p>
                      <label htmlFor="logo-upload" className="qx-file-btn">
                        {logoPreview || values.logo ? "Change Logo" : "Choose File"}
                      </label>
                      <Form.Control
                        id="logo-upload"
                        type="file"
                        accept="image/*"
                        className="d-none"
                        onChange={(e) => handleLogoChange(e, setFieldValue)}
                      />
                    </div>
                  </div>
                </div>

                {/* SECTION 2: Primary Details */}
                <div className="qx-form-section">
                  <div className="qx-form-section-head">
                    <span className="qx-form-section-num">2</span>
                    <div className="qx-form-section-head-text">
                      <h6 className="qx-form-section-title">Primary Details</h6>
                      <p className="qx-form-section-desc">Basic partner and company information</p>
                    </div>
                  </div>
                  <div className="qx-form-grid">
                    <FormField name="name" label="Partner Name" required placeholder="e.g. Jane Doe" />
                    <FormField name="companyName" label="Company Name" required placeholder="e.g. Acme Studios" />
                    <FormField name="email" label="Email Address (optional)"   placeholder="e.g. partner@example.com" />
                    <PhoneField disabled={false} />
                    <AlternatePhoneField disabled={false} />
                  </div>
                </div>

                {/* SECTION 3: Location & Business Info */}
                <div className="qx-form-section">
                  <div className="qx-form-section-head">
                    <span className="qx-form-section-num">3</span>
                    <div className="qx-form-section-head-text">
                      <h6 className="qx-form-section-title">Location & Business Info</h6>
                      <p className="qx-form-section-desc">Business type, city, GST, and acquisition source</p>
                    </div>
                  </div>
                  <div className="qx-form-grid qx-form-grid--3">
                    <BusinessTypeField />
                    <CitySelectField />
                    <FormField
                      name="gstNumber"
                      label="GST Number"
                       
                      placeholder="e.g. 22AAAAA0000A1Z5"
                      style={{ textTransform: "uppercase" }}
                    />
                    <AcquisitionSourceField />
                  </div>
                </div>

                {/* Optional Package Assignment (Creation Mode Only) */}
                {!isEdit && (
                  <div className="qx-form-section">
                    <div className="qx-form-section-head">
                      <span className="qx-form-section-num">4</span>
                      <div className="qx-form-section-head-text">
                        <h6 className="qx-form-section-title">
                          Package Assignment <span className="text-muted fw-normal">(optional)</span>
                        </h6>
                        <p className="qx-form-section-desc">Assign a partner package during creation</p>
                      </div>
                    </div>
                    <div className="qx-form-grid qx-form-grid--3">
                      <Form.Group controlId="subscriptionId" className="mb-0">
                        <Form.Label className="qx-form-label">Select Package</Form.Label>
                        <Form.Select
                          name="subscriptionId"
                          value={values.subscriptionId || ""}
                          disabled={loadingPackages}
                          onChange={(e) => {
                            const newId = e.target.value;
                            setFieldValue("subscriptionId", newId);
                            const pkgObj = packagesList.find((pkg) => (pkg._id || pkg.id) === newId);
                            if (pkgObj?.price !== undefined && pkgObj?.price !== null) {
                              setFieldValue("actualAmount", String(pkgObj.price));
                              setFieldValue("receivedMoney", String(pkgObj.price));
                            } else {
                              setFieldValue("actualAmount", "");
                              setFieldValue("receivedMoney", "");
                            }
                          }}
                          style={{ borderRadius: "8px", borderColor: "#d0d5dd", height: "44px" }}
                        >
                          <option value="">
                            {loadingPackages ? "Loading packages..." : "Select Package (Optional)"}
                          </option>
                          {packagesList.map((pkg) => (
                            <option key={pkg._id || pkg.id} value={pkg._id || pkg.id}>
                              {pkg.name || pkg.title} {pkg.price ? `(₹${pkg.price})` : ""}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>

                      <Form.Group className="mb-0">
                        <Form.Label className="qx-form-label">Actual Amount</Form.Label>
                        <Form.Control
                          type="text"
                          readOnly
                          disabled
                          value={values.actualAmount ? `₹ ${values.actualAmount}` : "-"}
                          style={{
                            borderRadius: "8px",
                            height: "44px",
                            backgroundColor: "#f9fafb",
                            color: "#667085",
                            fontWeight: "600",
                          }}
                        />
                      </Form.Group>

                      <Form.Group className="mb-0">
                        <Form.Label className="qx-form-label">Received Money</Form.Label>
                        <div style={{ position: "relative" }}>
                         
                          <Form.Control
                            type="text"
                            inputMode="decimal"
                            placeholder="Enter received amount"
                            value={values.receivedMoney || ""}
                            onChange={(e) => {
                              let value = e.target.value.replace(/[^0-9.]/g, "");
                              const parts = value.split(".");
                              if (parts.length > 2) {
                                value = parts[0] + "." + parts.slice(1).join("");
                              }
                              setFieldValue("receivedMoney", value);
                            }}
                            style={{
                              borderRadius: "8px",
                              borderColor: "#d0d5dd",
                              height: "44px",
                              paddingLeft: "30px",
                            }}
                          />
                        </div>
                      </Form.Group>
                    </div>
                  </div>
                )}

                {/* SECTION: Online Presence */}
                <div className="qx-form-section">
                  <div className="qx-form-section-head">
                    <span className="qx-form-section-num">{isEdit ? "4" : "5"}</span>
                    <div className="qx-form-section-head-text">
                      <h6 className="qx-form-section-title">Online Presence</h6>
                      <p className="qx-form-section-desc">Website and social links</p>
                    </div>
                  </div>
                  <div className="qx-form-grid">
                    <FormField name="website" label="Website URL (optional)" placeholder="eg. https://example.com" />
                    <FormField name="instagram" label="Instagram Link (optional)" placeholder="eg. https://instagram.com/yourpage" />
                  </div>
                </div>

                {/* Portfolio Images */}
                <div className="qx-form-section">
                  <div className="qx-form-section-head">
                    <span className="qx-form-section-num">{isEdit ? "5" : "6"}</span>
                    <div className="qx-form-section-head-text">
                      <h6 className="qx-form-section-title">Portfolio Images</h6>
                      <p className="qx-form-section-desc">Upload up to 10 portfolio images</p>
                    </div>
                    <span className={`qx-count-badge ms-auto ${galleryPreviews.length >= 10 ? "qx-count-badge--full" : ""}`}>
                      {galleryPreviews.length}/10
                    </span>
                  </div>

                  <label
                    htmlFor="portfolio-upload"
                    className={`qx-upload-zone ${galleryPreviews.length >= 10 ? "qx-upload-zone--disabled" : ""}`}
                  >
                    <UploadCloud size={28} className="qx-upload-zone-icon" />
                    <div className="qx-upload-zone-title">Click or drag images to upload</div>
                    <p className="qx-upload-zone-hint">
                      {isGalleryUploading
                        ? "Uploading..."
                        : galleryPreviews.length >= 10
                          ? "Maximum 10 portfolio images reached"
                          : "PNG or JPG images for the partner profile"}
                    </p>
                  </label>
                  <Form.Control
                    id="portfolio-upload"
                    type="file"
                    accept="image/*"
                    multiple
                    disabled={galleryPreviews.length >= 10 || isGalleryUploading}
                    className="d-none"
                    onChange={(e) => handleGalleryChange(e, values, setFieldValue)}
                  />

                  {galleryPreviews.length >= 10 && (
                    <small className="text-danger d-block mt-2 fw-semibold">
                      Maximum limit reached: You cannot add more than 10 portfolio images.
                    </small>
                  )}
                  {errors?.portfolioImages && (
                    <small className="text-danger d-block mt-2 fw-semibold">
                      {errors.portfolioImages}
                    </small>
                  )}

                  {galleryPreviews.length > 0 && (
                    <div className="qx-portfolio-grid">
                      {galleryPreviews.map((src, i) => (
                        <div key={src + i} className="qx-portfolio-thumb">
                          <img src={getImageUrl(src)} alt={`Portfolio preview ${i + 1}`} />
                          <button
                            type="button"
                            onClick={() => removeGalleryImage(i, values, setFieldValue)}
                            className="qx-portfolio-remove"
                            aria-label="Remove image"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="qx-form-actions">
                  
                  <Button
                    type="submit"
                    className="btn-primary qx-btn-brand"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        {partner ? "Saving…" : "Creating…"}
                      </>
                    ) : partner ? (
                      "Save Changes"
                    ) : (
                      "Create Partner"
                    )}
                  </Button>
                </div>
              </FormikForm>
            )}
          </Formik>
        </Card.Body>
      </Card>
    </div>
  );
}

